
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Components
import { Sidebar } from "@/components/Sidebar";
import { PageHeader } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

// Icons
import {
  Database,
  Upload,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  ImageIcon,
  Search,
  RefreshCw,
  Loader2,
  Home,
  MapPin,
  DollarSign,
  Building2,
} from "lucide-react";

// Hooks & Config
import { useToast } from "@/hooks/use-toast";
// import axiosInstance, { axiosSocial } from "@/config/axiosInstance";
import axiosInstance from "@/config/axiosInstance";

import { endpoints } from "@/config/apiEndpoints";

// Constants
const PROPERTY_TYPES = [
  { value: "family", label: "Family" },
  { value: "business", label: "Business" },
  { value: "commercial", label: "Commercial" },
] as const;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "complete", label: "Complete" },
] as const;

const IMPORT_OPTIONS = [
  {
    value: "manual",
    label: "Add Manually",
    description: "Enter property details manually",
    icon: Upload,
  },
  {
    value: "mls",
    label: "Import from MLS",
    description: "Auto-populate from public records",
    icon: Database,
  },
] as const;

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 1 MB
const MAX_PHOTOS = 4;
const MAX_GENERATION_ATTEMPTS = 3;

// Types
interface PropertyData {
  headline: string;
  address: string;
  price: string;
  bedrooms: string;
  bathrooms: string;
  sqft: string;
  description: string;
  propertyListType: string;
  propertyType: string;
  status: string;
}

interface PhotoItem {
  id: string;
  url: string;
  file: File;
}

interface LocationState {
  returnToStep?: number;
  fromCampaign?: string;
}

interface ValidationItem {
  label: string;
  isValid: boolean;
}

interface PropertyListType {
  id: string;
  name: string;
  [key: string]: any;
}

// Initial State
const initialPropertyData: PropertyData = {
  headline: "",
  address: "",
  price: "",
  bedrooms: "",
  bathrooms: "",
  sqft: "",
  description: "",
  propertyListType: "",
  propertyType: "",
  status: "",
};

// Loading Messages for MLS Search
const LOADING_MESSAGES = [
  "Connecting to property records...",
  "Searching database...",
  "Retrieving property information...",
  "Extracting property details...",
  "Loading property features...",
  "Fetching images...",
  "Almost there...",
];

const PropertyImport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  // Synchronous in-flight guard for handleAddressSearch — setIsSearchingMLS
  // is async/batched, so a double-click or double Enter before React
  // re-renders could otherwise fire two address-lookup API calls.
  const isSearchingMLSRef = useRef(false);

  // State
  const [importMethod, setImportMethod] = useState<"manual" | "mls">("manual");
  const [propertyData, setPropertyData] = useState<PropertyData>(initialPropertyData);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [propertyListTypes, setPropertyListTypes] = useState<PropertyListType[]>([]);
  const [selectedListType, setSelectedListType] = useState<string | null>(null);
  const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);
  const [mlsAddress, setMlsAddress] = useState("");
  const [isSearchingMLS, setIsSearchingMLS] = useState(false);
  const [searchProgress, setSearchProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [mlsSearchCompleted, setMlsSearchCompleted] = useState(false);

  const locationState = location.state as LocationState | null;
  const returnToStep = locationState?.returnToStep;
  const fromCampaign = locationState?.fromCampaign;

  // Loading message rotation effect
  useEffect(() => {
    if (!isSearchingMLS) return;

    let messageIndex = 0;
    let progress = 0;

    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 3000);

    const progressInterval = setInterval(() => {
      progress = Math.min(progress + Math.random() * 15, 90);
      setSearchProgress(progress);
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [isSearchingMLS]);

  // Fetch property list types on mount
  useEffect(() => {
    const fetchPropertyListTypes = async () => {
      try {
        const res = await axiosInstance.get(endpoints.propertylisttype);
        setPropertyListTypes(res.data);
      } catch (error) {
        console.error("Failed to load property list types:", error);
        toast({
          title: "Error",
          description: "Failed to load property list types",
          variant: "destructive",
        });
      }
    };
    fetchPropertyListTypes();
  }, [toast]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      photos.forEach((photo) => {
        if (photo.url) URL.revokeObjectURL(photo.url);
      });
    };
  }, []);

  // Generate unique ID
  const generateId = () => `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const createPhotoItemsFromUrls = useCallback(async (urls: string[]): Promise<PhotoItem[]> => {
    const limited = urls.slice(0, MAX_PHOTOS);
    const items: PhotoItem[] = [];

    for (let i = 0; i < limited.length; i += 1) {
      const url = limited[i];
      try {
        const response = await fetch(url);
        if (!response.ok) {
          console.warn(`Failed to load photo ${i + 1}: ${response.statusText}`);
          continue;
        }
        const blob = await response.blob();
        const extension = (blob.type && blob.type.split("/")[1]) || "jpg";
        const filename = `photo-${i + 1}.${extension}`;
        items.push({
          id: generateId(),
          url, // Keep the proxy URL for display
          file: new File([blob], filename, { type: blob.type || "image/jpeg" }),
        });
      } catch (error) {
        console.error(`Failed to load remote photo ${i + 1}:`, error);
        // Don't fail completely, just skip this photo
      }
    }

    return items;
  }, []);

  // Handlers
  const handleCancel = useCallback(() => {
    if (fromCampaign === "property" && returnToStep != null) {
      navigate("/builder", {
        state: {
          restoreStep: returnToStep,
          campaignType: "property",
        },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  }, [fromCampaign, returnToStep, navigate]);

  const handleInputChange = useCallback(
    (field: keyof PropertyData, value: string) => {
      setPropertyData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const validatePositiveInteger = useCallback(
    (label: string, value: string): boolean => {
      if (value === "") return true;

      const num = Number(value);

      if (!Number.isInteger(num)) {
        toast({
          title: `${label} must be a whole number`,
          description: "Decimal values are not allowed.",
          variant: "destructive",
        });
        return false;
      }

      if (num < 0) {
        toast({
          title: `${label} cannot be negative`,
          description: "Please enter a positive number.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    },
    [toast]
  );

  const handleIntegerChange = useCallback(
    (field: keyof PropertyData, value: string, label: string) => {
      if (value && !/^\d*$/.test(value)) {
        return;
      }

      if (!validatePositiveInteger(label, value)) {
        return;
      }

      handleInputChange(field, value);
    },
    [validatePositiveInteger, handleInputChange]
  );

  const handleRegenerateDescription = useCallback(async () => {
    if (isGeneratingDesc || generationCount >= MAX_GENERATION_ATTEMPTS) return;

    setIsGeneratingDesc(true);

    try {
      const resp = await axiosInstance.post(endpoints.generate_description, {
        address: propertyData.address,
        price: propertyData.price
          ? parseInt(propertyData.price.replace(/[^0-9.-]+/g, ""))
          : 0,
        headline: propertyData.headline,
        bedrooms: propertyData.bedrooms ? parseInt(propertyData.bedrooms) : 0,
        bathrooms: propertyData.bathrooms ? parseInt(propertyData.bathrooms) : 0,
        area: propertyData.sqft ? parseInt(propertyData.sqft) : 0,
        features: "",
      });

      const description = resp.data?.description?.trim();

      if (description) {
        setPropertyData((prev) => ({ ...prev, description }));
        toast({
          title: "Description Generated",
          description: "AI-powered description added!",
        });
        setGenerationCount((prev) => prev + 1);
      }
    } catch (err: any) {
      toast({
        title: "Generation Failed",
        description: err.message || "Unable to generate description",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingDesc(false);
    }
  }, [isGeneratingDesc, generationCount, propertyData, toast]);

  const getGenerateButtonText = useCallback(() => {
    if (generationCount === 0) return "Generate";
    if (generationCount < MAX_GENERATION_ATTEMPTS) return "Regenerate";
    return "Limit Reached";
  }, [generationCount]);

  // Process files for upload
  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const remainingSlots = MAX_PHOTOS - photos.length;

      if (remainingSlots <= 0) {
        toast({
          title: "Maximum Photos Reached",
          description: `You can only upload up to ${MAX_PHOTOS} photos.`,
          variant: "destructive",
        });
        return;
      }

      const filesToProcess = fileArray.slice(0, remainingSlots);
      const skippedCount = fileArray.length - filesToProcess.length;

      const validPhotos: PhotoItem[] = [];

      filesToProcess.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          toast({
            title: "Invalid File Type",
            description: `${file.name} is not an image file.`,
            variant: "destructive",
          });
          return;
        }

        // Line ~274 - Update the error message in processFiles function
if (file.size > MAX_IMAGE_SIZE) {
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
  toast({
    title: "File Too Large",
    description: `${file.name} (${fileSizeMB} MB) exceeds 10 MB limit.`, // changed from 1 MB
    variant: "destructive",
  });
  return;
}

        const url = URL.createObjectURL(file);
        validPhotos.push({
          id: generateId(),
          url,
          file,
        });
      });

      if (validPhotos.length > 0) {
        setPhotos((prev) => [...prev, ...validPhotos]);
        toast({
          title: "Photos Added",
          description: `${validPhotos.length} photo${validPhotos.length > 1 ? "s" : ""} uploaded successfully!`,
        });
      }

      if (skippedCount > 0) {
        toast({
          title: "Some Photos Skipped",
          description: `${skippedCount} photo${skippedCount > 1 ? "s were" : " was"} skipped. Maximum ${MAX_PHOTOS} photos allowed.`,
          variant: "destructive",
        });
      }
    },
    [photos.length, toast]
  );

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
      event.target.value = "";
    },
    [processFiles]
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [processFiles]
  );

  const removePhoto = useCallback(
    (photoId: string) => {
      setPhotos((prev) => {
        const photoToRemove = prev.find((p) => p.id === photoId);
        if (photoToRemove) {
          URL.revokeObjectURL(photoToRemove.url);
        }
        return prev.filter((p) => p.id !== photoId);
      });

      toast({
        title: "Photo Removed",
        description: "Photo has been removed.",
      });
    },
    [toast]
  );

  const setAsMainPhoto = useCallback(
    (photoId: string) => {
      setPhotos((prev) => {
        const photoIndex = prev.findIndex((p) => p.id === photoId);
        if (photoIndex <= 0) return prev;

        const newPhotos = [...prev];
        const [photo] = newPhotos.splice(photoIndex, 1);
        newPhotos.unshift(photo);
        return newPhotos;
      });

      toast({
        title: "Main Photo Updated",
        description: "Photo has been set as the main image.",
      });
    },
    [toast]
  );

  const getValidationItems = useCallback((): ValidationItem[] => {
    return [
      { label: "Title", isValid: !!propertyData.headline },
      { label: "Address", isValid: !!propertyData.address },
      { label: "Price", isValid: !!propertyData.price },
      { label: "Bedrooms", isValid: !!propertyData.bedrooms },
      { label: "Bathrooms", isValid: !!propertyData.bathrooms },
      { label: "Description", isValid: !!propertyData.description },
      { label: "Property Type", isValid: !!propertyData.propertyType },
      { label: "Status", isValid: !!propertyData.status },
      {
        label: `Images (${photos.length}/${MAX_PHOTOS})`,
        isValid: photos.length >= 1,
      },
    ];
  }, [propertyData, photos.length]);

  const handleAddProperty = useCallback(async () => {
    const validationItems = getValidationItems();
    const missingFields = validationItems
      .filter((item) => !item.isValid)
      .map((item) => item.label);
  
    if (missingFields.length > 0) {
      toast({
        title: "Missing Required Fields",
        description: `Please fill: ${missingFields.join(", ")}`,
        variant: "destructive",
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      const formData = new FormData();
  
      // FIXED: Use the full headline as title (this is what user sees everywhere)
      const title = propertyData.headline.trim() || propertyData.address.split(",")[0].trim();
      formData.append("title", title);
  
      formData.append("address", propertyData.address);
  
      const cleanPrice = parseInt(propertyData.price.replace(/[^0-9.-]+/g, ""), 10);
      if (!isNaN(cleanPrice)) {
        formData.append("price", cleanPrice.toString());
      }
  
      formData.append("bedrooms", propertyData.bedrooms);
      formData.append("bathrooms", propertyData.bathrooms);
      if (propertyData.sqft) formData.append("area", propertyData.sqft);
  
      formData.append("description", propertyData.description);
      formData.append("property_type", propertyData.propertyType || "family");
      formData.append("status", propertyData.status || "active");
  
      // Always send propertyListType_id (MLS flow never sets it otherwise)
      let listTypeId = selectedListType;
      if (!listTypeId && propertyListTypes.length > 0) {
        listTypeId = propertyListTypes[0].id; // fallback to first available
      }
      if (listTypeId) {
        formData.append("propertyListType_id", listTypeId);
      }
  
      // Photos
      photos.forEach((photo, index) => {
        if (index === 0) {
          formData.append("image", photo.file);
        } else {
          formData.append(`photo${index + 1}`, photo.file);
        }
      });
  
      await axiosInstance.post(endpoints.properties, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
  
      toast({
        description: (
          <span className="font-semibold text-green-700">
            Property added successfully!
          </span>
        ),
      });
  
      // Go back to campaign builder correctly
      setTimeout(() => {
        if (fromCampaign === "property" && returnToStep != null) {
          navigate("/builder", {
            state: { restoreStep: returnToStep, campaignType: "property" },
            replace: true,
          });
        } else {
          navigate("/builder");
        }
      }, 1500);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to add property",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    propertyData,
    photos,
    selectedListType,
    propertyListTypes,
    fromCampaign,
    returnToStep,
    navigate,
    toast,
    getValidationItems,
  ]);

  const handleAddressSearch = useCallback(async () => {
    if (isSearchingMLSRef.current) return;

    if (!mlsAddress.trim()) {
      toast({
        title: "Error",
        description: "Please enter the property address",
        variant: "destructive",
      });
      return;
    }

    isSearchingMLSRef.current = true;
    setIsSearchingMLS(true);
    setSearchProgress(0);
    setLoadingMessage(LOADING_MESSAGES[0]);
    setMlsSearchCompleted(false);

    try {

      const token = localStorage.getItem("access_token");
      const headers: any = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axiosSocial.post(
        endpoints.address_lookup,
        { address: mlsAddress.trim() },
        { headers }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.error || "Property not found");
      }

      const normalized = response.data?.normalized || {};
      const photosFromApi: string[] = response.data?.photos || [];

      // Inside handleAddressSearch → after setPropertyData
      setPropertyData((prev) => ({
        ...prev,
        headline: normalized.headline || normalized.address || mlsAddress, 
        address: normalized.address || mlsAddress,
        price: normalized.price || "",
        bedrooms: normalized.bedrooms || "",
        bathrooms: normalized.bathrooms || "",
        sqft: normalized.sqft || "",
        description: prev.description || "Stunning property imported from public records.",
        propertyType: prev.propertyType || "family",
        status: prev.status || "active",
      }));

      if (photosFromApi.length > 0) {
        const items = await createPhotoItemsFromUrls(photosFromApi);
        if (items.length > 0) {
          setPhotos(items);
        }
      }

      setSearchProgress(100);
      setMlsSearchCompleted(true);

      toast({
        title: "Property Found",
        description: "Property data has been populated from public records.",
      });
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        error.message ||
        "Unable to import property. Please try again or enter details manually.";

      toast({
        title: "Import Failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSearchingMLS(false);
      setSearchProgress(0);
      isSearchingMLSRef.current = false;
    }
  }, [mlsAddress, toast, createPhotoItemsFromUrls]);

  const handleResetMlsSearch = useCallback(() => {
    setPropertyData(initialPropertyData);
    setPhotos([]);
    setMlsAddress("");
    setMlsSearchCompleted(false);
    setGenerationCount(0);
  }, []);

  // Handle import method change
  const handleImportMethodChange = useCallback((value: string) => {
    setImportMethod(value as "manual" | "mls");
    // Reset form when switching methods
    setPropertyData(initialPropertyData);
    setPhotos([]);
    setMlsAddress("");
    setMlsSearchCompleted(false);
    setGenerationCount(0);
  }, []);

  // MLS Search Loading Overlay Component
  const MLSSearchLoader = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="w-full max-w-md mx-4 shadow-2xl border-0">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="h-20 w-20 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Database className="h-8 w-8 text-primary animate-pulse" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold text-foreground">
                Searching Property Records
              </h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Looking up "<span className="font-medium text-foreground">{mlsAddress}</span>" in public records...
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="animate-pulse">{loadingMessage}</span>
            </div>

            <div className="w-full space-y-2">
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
                  style={{ width: `${searchProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-muted-foreground">
                This may take 15-30 seconds
              </p>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100 w-full">
              <AlertCircle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700">
                <p className="font-medium">Please wait</p>
                <p className="mt-0.5 opacity-80">
                  We're retrieving property data from Orange County Property Appraiser records.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Import Method Selector Component (Radio Buttons)
  const ImportMethodSelector = () => (
    <div className="space-y-3">
      <Label className="text-base font-medium">How would you like to add the property?</Label>
      <RadioGroup
        value={importMethod}
        onValueChange={handleImportMethodChange}
        className="grid gap-3 sm:grid-cols-2"
      >
        {IMPORT_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = importMethod === option.value;
          return (
            <label
              key={option.value}
              htmlFor={option.value}
              className={`
                relative flex cursor-pointer items-start gap-4 rounded-xl border-2 p-4 transition-all duration-200
                ${
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border bg-background hover:border-primary/40 hover:bg-muted/30"
                }
              `}
            >
              <RadioGroupItem
                value={option.value}
                id={option.value}
                className="mt-1"
              />
              <div className="flex flex-1 items-start gap-3">
                <div
                  className={`
                    flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors
                    ${
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p
                    className={`font-semibold leading-tight ${
                      isSelected ? "text-primary" : "text-foreground"
                    }`}
                  >
                    {option.label}
                  </p>
                  <p className="text-sm text-muted-foreground leading-snug">
                    {option.description}
                  </p>
                </div>
              </div>
              {isSelected && (
                <div className="absolute right-3 top-3">
                  <CheckCircle className="h-5 w-5 text-primary" />
                </div>
              )}
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );

  // Render Components
  const renderValidationSection = () => {
    const validationItems = getValidationItems();
    const completedCount = validationItems.filter((item) => item.isValid).length;
    const totalCount = validationItems.length;
    const isComplete = completedCount === totalCount;

    return (
      <div
        className={`rounded-lg border p-4 transition-colors ${
          isComplete
            ? "border-green-200 bg-green-50"
            : "border-border bg-muted/30"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-primary">Validation Status</h4>
          <span
            className={`text-sm font-medium ${
              isComplete ? "text-green-600" : "text-muted-foreground"
            }`}
          >
            {completedCount}/{totalCount} complete
          </span>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-2">
          {validationItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              {item.isValid ? (
                <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span
                className={
                  item.isValid ? "text-foreground" : "text-muted-foreground"
                }
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderImageUpload = () => {
    const canAddMore = photos.length < MAX_PHOTOS;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label>Property Images *</Label>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload up to {MAX_PHOTOS} photos. First photo will be the main image.
            </p>
          </div>
          <span
            className={`text-sm font-medium ${
              photos.length >= 1 ? "text-green-600" : "text-muted-foreground"
            }`}
          >
            {photos.length}/{MAX_PHOTOS} photos
          </span>
        </div>

        {canAddMore && (
          <div
            ref={dropZoneRef}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative cursor-pointer rounded-lg border-2 border-dashed p-8
              transition-all duration-200 ease-in-out
              ${
                isDragging
                  ? "border-primary bg-primary/5 scale-[1.02]"
                  : "border-border bg-muted/30 hover:border-primary/50 hover:bg-muted/50"
              }
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />

            <div className="flex flex-col items-center justify-center text-center">
              <div
                className={`
                  mb-4 rounded-full p-4 transition-colors
                  ${isDragging ? "bg-primary/10" : "bg-muted"}
                `}
              >
                <Upload
                  className={`h-8 w-8 ${
                    isDragging ? "text-primary" : "text-muted-foreground"
                  }`}
                />
              </div>

              <p className="text-base font-medium text-foreground">
                {isDragging ? "Drop your photos here" : "Drag & drop photos here"}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                or <span className="text-primary font-medium">click to browse</span>
              </p>
              // Line ~695 - Update the drop zone text
<p className="mt-3 text-xs text-muted-foreground">
  PNG, JPG up to 10 MB each • Maximum {MAX_PHOTOS - photos.length} more
  photo
  {MAX_PHOTOS - photos.length !== 1 ? "s" : ""}
</p>
            </div>
          </div>
        )}

        {photos.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-muted-foreground">
                Uploaded Photos
              </Label>
              {photos.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  Click "Set as Main" to change the primary photo
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {photos.map((photo, index) => (
                <div
                  key={photo.id}
                  className={`
                    group relative overflow-hidden rounded-lg border-2 transition-all
                    ${index === 0 ? "border-primary ring-2 ring-primary/20" : "border-border"}
                  `}
                >
                  {index === 0 && (
                    <div className="absolute left-2 top-2 z-10 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground shadow-md">
                      Main
                    </div>
                  )}

                  <div className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs font-medium text-white">
                    {index + 1}
                  </div>

                  <div className="aspect-video">
                    <img
                      src={photo.url}
                      alt={`Property photo ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                    {index !== 0 && (
                      <button
                        type="button"
                        onClick={() => setAsMainPhoto(photo.id)}
                        className="rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-white/30"
                      >
                        Set as Main
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      className="rounded-lg bg-red-500/80 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-red-600"
                    >
                      <X className="mr-1 inline-block h-3 w-3" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {canAddMore && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-video cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 transition-all hover:border-primary/50 hover:bg-muted/50"
                >
                  <Plus className="mb-1 h-6 w-6 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Add Photo
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {!canAddMore && (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Maximum {MAX_PHOTOS} photos uploaded.</span>
          </div>
        )}
      </div>
    );
  };

  const renderDescriptionField = () => (
    <div className="space-y-3">
      <Label htmlFor="description">Description *</Label>
      <Textarea
        id="description"
        placeholder="Enter a compelling property description..."
        rows={8}
        value={propertyData.description}
        onChange={(e) => handleInputChange("description", e.target.value)}
        className="resize-none"
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleRegenerateDescription}
            disabled={
              generationCount >= MAX_GENERATION_ATTEMPTS || isGeneratingDesc
            }
            className={`
              flex items-center gap-1.5 text-xs font-medium transition-all
              ${
                generationCount >= MAX_GENERATION_ATTEMPTS
                  ? "cursor-not-allowed bg-muted text-muted-foreground"
                  : "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
              }
            `}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isGeneratingDesc ? "animate-spin" : ""}`}
            />
            {getGenerateButtonText()}
          </Button>

          {generationCount > 0 && generationCount < MAX_GENERATION_ATTEMPTS && (
            <span className="text-xs font-medium text-orange-600">
              {MAX_GENERATION_ATTEMPTS - generationCount} attempt
              {MAX_GENERATION_ATTEMPTS - generationCount > 1 ? "s" : ""} left
            </span>
          )}
        </div>

        {generationCount === 0 && (
          <p className="text-xs text-muted-foreground">
            Click "Generate" for an AI-powered description
          </p>
        )}
      </div>

      {generationCount >= MAX_GENERATION_ATTEMPTS && (
        <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-xs text-orange-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>You have reached the description generation limit.</span>
        </div>
      )}
    </div>
  );

  const renderPropertyForm = () => (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="headline">Property Title *</Label>
          <Input
            id="headline"
            placeholder="Enter property title..."
            value={propertyData.headline}
            onChange={(e) => handleInputChange("headline", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Address *</Label>
          <Input
            id="address"
            placeholder="Enter full address..."
            value={propertyData.address}
            onChange={(e) => handleInputChange("address", e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="price">Price *</Label>
          <Input
            id="price"
            type="text"
            maxLength={12}
            inputMode="numeric"
            placeholder="Enter price..."
            value={propertyData.price}
            onChange={(e) => handleIntegerChange("price", e.target.value, "Price")}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-2">
            <Label htmlFor="bedrooms">Bedrooms *</Label>
            <Input
              id="bedrooms"
              type="text"
              maxLength={3}
              inputMode="numeric"
              placeholder="3"
              value={propertyData.bedrooms}
              onChange={(e) =>
                handleIntegerChange("bedrooms", e.target.value, "Bedrooms")
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bathrooms">Bathrooms *</Label>
            <Input
              id="bathrooms"
              type="text"
              maxLength={3}
              inputMode="numeric"
              placeholder="2"
              value={propertyData.bathrooms}
              onChange={(e) =>
                handleIntegerChange("bathrooms", e.target.value, "Bathrooms")
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sqft">Sq. Ft.</Label>
            <Input
              id="sqft"
              type="text"
              maxLength={8}
              inputMode="numeric"
              placeholder="2000"
              value={propertyData.sqft}
              onChange={(e) =>
                handleIntegerChange("sqft", e.target.value, "Sq. Ft.")
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="propertyType">Property Type *</Label>
            <Select
              value={propertyData.propertyType}
              onValueChange={(value) => handleInputChange("propertyType", value)}
            >
              <SelectTrigger id="propertyType">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          
        </div>
      </div>

      <div className="space-y-4">{renderDescriptionField()}</div>
    </div>
  );

  const renderActionButtons = (
    onSubmit: () => void,
    isSubmitting: boolean,
    submitLabel: string,
    submitIcon: React.ReactNode
  ) => (
    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        type="button"
        variant="default"
        size="lg"
        onClick={onSubmit}
        disabled={isSubmitting}
        className="bg-primary hover:bg-primary/90"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            {submitIcon}
            {submitLabel}
          </>
        )}
      </Button>
    </div>
  );

  // MLS Empty State Component
  const MLSEmptyState = () => (
    <Card className="border-dashed">
      <CardContent className="py-16">
        <div className="text-center text-muted-foreground">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
            <Building2 className="h-10 w-10 opacity-50" />
          </div>
          <p className="text-lg font-medium text-foreground">No property loaded</p>
          <p className="mt-2 text-sm max-w-sm mx-auto">
            Enter a property address above to search public records and automatically
            populate property details.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <Home className="h-4 w-4" />
              <span>Property Details</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              <span>Address Info</span>
            </div>
            <div className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4" />
              <span>Value Data</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ImageIcon className="h-4 w-4" />
              <span>Property Images</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // MLS Search Success Banner
  const MLSSuccessBanner = () => (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-green-200 bg-green-50 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
        <div>
          <p className="font-medium text-green-800">Property Found</p>
          <p className="text-sm text-green-600">
            Data populated from public records. Review and edit as needed.
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleResetMlsSearch}
        className="shrink-0 border-green-300 text-green-700 hover:bg-green-100"
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        New Search
      </Button>
    </div>
  );

  // Render MLS Search Section
  const renderMLSSearchSection = () => (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label htmlFor="mlsAddress">Property Address *</Label>
        <p className="text-sm text-muted-foreground">
          Enter the property address to search public records
          (Orange County Property Appraiser)
        </p>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Input
              id="mlsAddress"
              placeholder="e.g., 2812 S Shine Ave, Orlando, FL"
              value={mlsAddress}
              onChange={(e) => setMlsAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isSearchingMLS && mlsAddress.trim()) {
                  handleAddressSearch();
                }
              }}
              disabled={isSearchingMLS}
              className="pr-10"
            />
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
          <Button
            type="button"
            variant="default"
            onClick={handleAddressSearch}
            disabled={isSearchingMLS || !mlsAddress.trim()}
            className="shrink-0 min-w-[120px]"
          >
            {isSearchingMLS ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Search
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Search typically takes 15-30 seconds
        </p>
      </div>

      {mlsSearchCompleted && (propertyData.address || propertyData.headline) && (
        <MLSSuccessBanner />
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {isSearchingMLS && <MLSSearchLoader />}

      <Sidebar />

      <main className="flex flex-1 flex-col">
        <PageHeader
          title="Property Integration"
          subtitle="Import property data from MLS or enter manually"
        />

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mx-auto max-w-4xl space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {importMethod === "manual" ? (
                    <>
                      <Upload className="h-5 w-5" />
                      Add Property
                    </>
                  ) : (
                    <>
                      <Database className="h-5 w-5" />
                      Import Property
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Import Method Radio Buttons */}
                <ImportMethodSelector />

                <hr className="border-border" />

                {/* MLS Search Section - Only show for MLS method */}
                {importMethod === "mls" && renderMLSSearchSection()}

                {/* Property Form - Show for manual, or after MLS search success */}
                {importMethod === "manual" || propertyData.address || propertyData.headline ? (
                  <>
                    {importMethod === "mls" && (propertyData.address || propertyData.headline) && (
                      <hr className="border-border" />
                    )}
                    {renderPropertyForm()}
                    {renderImageUpload()}
                    {renderValidationSection()}
                    {renderActionButtons(
                      handleAddProperty,
                      isSaving,
                      "Add Property",
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                  </>
                ) : (
                  importMethod === "mls" && <MLSEmptyState />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertyImport;
